/**
 * Route guard for the Lead-to-Appointment cockpit
 * (LEAD-TO-APPOINTMENT-COCKPIT-V1). Mirrors RequireWorkboard: the pure decision
 * lives in decideLeadCockpitAccess (lead-cockpit-view.ts) and keys off the
 * caller's role - receptionist / coc (the front-half operator pair). Any other
 * role redirects to its own homePath. This is a role-only gate: NO /me flag
 * signal, NO feature flag, NO new nav section (internal surface, default-on per
 * ADR-0016).
 *
 * Backend route auth (the POST /api/appointments/booked + POST
 * /api/leads/:id/stage + POST /api/work-actions/:id/transition role/scope gates)
 * remains the real security boundary; this guard is UX cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideLeadCockpitAccess } from '@/dashboard/lead-cockpit/lead-cockpit-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireLeadCockpitViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireLeadCockpitView({
  loading,
  error,
  me,
  children,
}: RequireLeadCockpitViewProps): JSX.Element | null {
  const decision = decideLeadCockpitAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireLeadCockpit(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireLeadCockpitView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
